// ABOUTME: Session-token support for the native wrapper and admin/support access: reads tokens handed over
// ABOUTME: at bootstrap, keeps them in storage, and exposes the headers the API client and REST downloads attach.
import { STORAGE_KEYS } from "@shared/constants/storageKeys"

/**
 * The localStorage keys that carry a session. This is the ONE list: logout clears
 * them, isSetSupportToken checks them, init ingests them from the URL, and
 * RealtimeProvider restarts the realtime session when another tab writes one.
 * A new credential goes here and nowhere else.
 */
export const SESSION_STORAGE_KEYS = [
  STORAGE_KEYS.SESSION_TOKEN_2,
  STORAGE_KEYS.COOKIE_SUPPORT,
  STORAGE_KEYS.KRATOS_SESSION_TOKEN,
] as const

// True only inside the native app's WebView. Must be readable at bootstrap
// (authSupport.init runs before first paint), so it CANNOT use
// window.webkit.messageHandlers.nativeBridge — Android injects that late
// (onPageFinished). Android instead sets `__TE_AUTH_NATIVE__` at document-start;
// iOS (WKWebView) exposes its message handler from the start.
function inNativeWrapper(): boolean {
  if (typeof window === "undefined") return false
  const w = window as typeof window & { __TE_AUTH_NATIVE__?: boolean }
  return w.__TE_AUTH_NATIVE__ === true || w.webkit?.messageHandlers.nativeBridge !== undefined
}

/**
 * Ingests an auth token delivered in the URL into localStorage, then strips it.
 *
 * @param nativeOnly when true, the token is a raw session credential the native
 *   wrapper injects (after its own single-use nonce check). In a plain browser
 *   this app authenticates via cookies, so a raw token in the URL is illegitimate
 *   — a planted session-fixation link — and is IGNORED (not stored). It is still
 *   stripped from the URL either way, so it never reaches analytics/session
 *   recorders that capture $current_url.
 */
function CheckAuthTokenSession(name: string, nativeOnly = false) {
  const params = new URLSearchParams(window.location.search)
  const token = params.get(name)
  if (!token) {
    return
  }

  if (!nativeOnly || inNativeWrapper()) {
    // No decodeURIComponent: URLSearchParams.get already decoded this. A second pass would eat a
    // percent sequence the token legitimately contains — "%252F" arrives here as "%2F" and would
    // be stored as "/".
    localStorage.setItem(name, token)
  }

  // Never leave the credential in the URL — strip it before Mixpanel/Clarity/
  // Sentry initialise (init() runs before them). Done even when ignored above.
  params.delete(name)
  const qs = params.toString()
  window.history.replaceState(
    window.history.state,
    "",
    window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash,
  )
}

class AuthSupport {
  clean() {
    for (const key of SESSION_STORAGE_KEYS) localStorage.removeItem(key)
  }

  isSetSupportToken() {
    return SESSION_STORAGE_KEYS.some((key) => !!localStorage.getItem(key))
  }

  init() {
    // All of these are session-establishing credentials. In a plain browser this
    // app authenticates via cookies, so ANY of them arriving in the URL is
    // illegitimate (a session-fixation link) — ingest only inside the native
    // wrapper, which injects them after its own single-use nonce check. Each is
    // still stripped from the URL either way (no leak to analytics).
    for (const key of SESSION_STORAGE_KEYS) CheckAuthTokenSession(key, true)
  }

  getSessionToken2() {
    return localStorage.getItem(STORAGE_KEYS.SESSION_TOKEN_2) || undefined
  }

  getCookieToken() {
    return localStorage.getItem(STORAGE_KEYS.COOKIE_SUPPORT) || undefined
  }

  getKratosSessionToken() {
    return localStorage.getItem(STORAGE_KEYS.KRATOS_SESSION_TOKEN) || undefined
  }
}

const authSupport = new AuthSupport()

export default authSupport
